import { ReactNode } from 'react';
import classNames from 'classnames';

import { Toggle } from '@/components/form/toggle';
import { SearchInput } from '@/components/form/search-input';

import { Aggregation, AggregationExtendedType, Fields, Metric, Sort } from '../types';
import {
  isDeduplicatableAggregation,
  isKeyedAggregation,
  isSortedAggregation,
} from '../pipeline/operation';
import { PipelineEditorError } from './errors';
import { metricAggregationOptions } from '../utils/metrics';
import {
  aggregationTypeToAggregationExtendedType,
  getAggregationOptionsForFields,
  getFieldOptionsForAggregationType,
} from '../utils/aggregation';

import { SortingEditor } from './sorting-editor';

import form from '../../components/form/form.module.scss';

interface AggregationFormProps {
  accessory: ReactNode;
  aggregation: Aggregation;
  autoFocus?: boolean;
  excludeAggregationTypes?: AggregationExtendedType[];
  fields: Fields;
  metrics: Metric[];
  onChangeType: (type: AggregationExtendedType) => void;
  onChangeField: (key: string) => void;
  onChangeSorting: (sort: Sort) => void;
  onChangeDistinct: (distinct: boolean) => void;
}

export const AggregationForm = (props: AggregationFormProps) => {
  const { aggregation, fields, metrics, excludeAggregationTypes = [], autoFocus = false } = props;
  const isKeyed = isKeyedAggregation(aggregation);
  const field = isKeyed ? fields.find((f) => f.key === aggregation.key) : undefined;
  if (field === undefined && isKeyed) {
    throw new PipelineEditorError(
      `Keyed summarization references unknown field with key '${aggregation.key}'`,
    );
  }

  const aggregationExtendedType = aggregationTypeToAggregationExtendedType(aggregation.type, field);
  if (aggregationExtendedType === undefined) {
    throw new PipelineEditorError(`Unknown summarization type '${aggregation.type}'`);
  }

  const options = getAggregationOptionsForFields(fields)
    .filter(({ value }) => !excludeAggregationTypes.includes(value))
    .map(({ label, value }) => ({ label, value: value.toString() }))
    .concat(metricAggregationOptions(metrics));

  const value = aggregation.type === 'metric' ? aggregation.metricId : aggregationExtendedType;

  return (
    <div className={form.formHorizontal}>
      <div className={classNames(form.formRow, form.alignTop)}>
        <label className={form.formLabel}>Show</label>
        <SearchInput
          options={options}
          value={value}
          autoFocus={autoFocus}
          closeOnSelect
          onChange={(v) => props.onChangeType(v as AggregationExtendedType)}
        />
        {props.accessory}
      </div>
      {isKeyedAggregation(aggregation) && (
        <div className={classNames(form.formRow)}>
          <label className={form.formLabel}>of</label>
          <SearchInput
            options={getFieldOptionsForAggregationType(aggregationExtendedType, fields)}
            value={aggregation.key}
            closeOnSelect
            onChange={props.onChangeField}
          />
        </div>
      )}
      {isDeduplicatableAggregation(aggregation) && (
        <div className={classNames(form.formRow)}>
          <label
            className={form.formLabel}
            onClick={() => props.onChangeDistinct(!(aggregation.distinct ?? false))}>
            Deduplicate
          </label>
          <Toggle
            checked={aggregation.distinct === true}
            onChange={(checked) => props.onChangeDistinct(checked)}
            size="small"
          />
        </div>
      )}
      {isSortedAggregation(aggregation) && (
        <div className={classNames(form.formRow)}>
          <label className={form.formLabel}>sorted by</label>
          <SortingEditor
            fields={fields}
            sorting={aggregation.sort}
            setSorting={props.onChangeSorting}
          />
        </div>
      )}
    </div>
  );
};
